/**
 * Validates start and end times, ensuring end time is not before start time.
 * Handles midnight crossing (e.g., 11pm to 3am is valid).
 * Detects likely mistakes (e.g., 5pm to 7am should probably be 7pm).
 *
 * @param {string} startTime - Start time in HH:MM or HH:MM:SS format
 * @param {string} endTime - End time in HH:MM or HH:MM:SS format
 * @param {Object} options - Optional configuration
 * @param {boolean} options.allowSameTime - Allow start and end to be the same (default: false)
 * @returns {{ isValid: boolean, error: string|null, warning: string|null }}
 */
function validateTimeRange(startTime, endTime, options = {}) {
  const { allowSameTime = false } = options;

  if (!startTime || !endTime) {
    return { isValid: true, error: null, warning: null };
  }

  const normalizeTime = (time) => {
    if (!time) return null;
    return time.length >= 5 ? time.substring(0, 5) : time;
  };

  const start = normalizeTime(startTime);
  const end = normalizeTime(endTime);

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);

  if (startMinutes === null || endMinutes === null) {
    return { isValid: false, error: "Invalid time format", warning: null };
  }

  if (startMinutes === endMinutes) {
    if (!allowSameTime) {
      return {
        isValid: false,
        error: "Start time and end time cannot be the same",
        warning: null,
      };
    }
    return { isValid: true, error: null, warning: null };
  }

  let timeDiff;
  let crossesMidnight = false;

  if (endMinutes < startMinutes) {
    crossesMidnight = true;
    timeDiff = 24 * 60 - startMinutes + endMinutes;
  } else {
    timeDiff = endMinutes - startMinutes;
  }

  if (crossesMidnight) {
    const endHour = Math.floor(endMinutes / 60);
    // AM/PM mistake heuristic: start must be 1:00 PM or later (not noon or lunch hour).
    // 12:00–12:59 would otherwise pair with "long overnight" + morning end and misfire (e.g. noon–1am).
    const startIsAfternoonOrEvening = startMinutes >= 13 * 60;

    if (timeDiff > 12 * 60) {
      if (endHour < 12 && startIsAfternoonOrEvening) {
        const suggestedEndHour = endHour + 12;
        const suggestedEnd = `${String(suggestedEndHour).padStart(2, "0")}:${end.substring(3)}`;
        return {
          isValid: false,
          error: `End time (${end}) appears to be before start time (${start}). Did you mean ${suggestedEnd} (PM) instead of ${end} (AM)?`,
          warning: null,
        };
      }

      if (timeDiff > 18 * 60) {
        return {
          isValid: false,
          error: `The time range crosses midnight and is very long (${Math.round(timeDiff / 60)} hours). Please verify both times are correct.`,
          warning: null,
        };
      }
    }

    return { isValid: true, error: null, warning: "Time range crosses midnight" };
  }

  return { isValid: true, error: null, warning: null };
}

function formatTimeForDisplay(time) {
  if (!time) return "";
  const normalized = time.length >= 5 ? time.substring(0, 5) : time;
  const [hours, minutes] = normalized.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;

  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHour}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

module.exports = {
  validateTimeRange,
  formatTimeForDisplay,
};
